import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import ImageCropped from 'views_shared/app/javascript/components/ImageCropped';
import ProposedSourceChip from 'views_shared/app/javascript/components/ProposedSourceChip';
import { isPeerDmOrigin } from 'views_shared/app/javascript/constants/conversationOriginTypes';
import { profileUrl, expertUrl, productUrl } from 'views_shared/app/javascript/utils/urls';

import AddToConversationButton from './AddToConversationButton';
import ConversationDropdown from '../ConversationDropdown';
import useConversationTeammates from './useConversationTeammates';
import mayStartConversations from '../mayStartConversations';

const HEADER_NAME_LIMIT = 3;

const formatProfileNames = (profiles, urlFor = profileUrl) => {
  const visible = profiles.slice(0, HEADER_NAME_LIMIT);
  const overflowCount = profiles.length - visible.length;
  return (
    <>
      {visible.map((p, index) => (
        <React.Fragment key={p.slug}>
          <a
            href={urlFor(p)}
            target="_blank"
            rel="noopener noreferrer"
            className="paragraph-lg fw-bold text-decoration-none conversation-header-profile-link lh-1"
          >
            {p.name}
          </a>
          {index < visible.length - 1 && ', '}
        </React.Fragment>
      ))}
      {overflowCount > 0 && (
        <span className="paragraph-lg fw-bold lh-1">
          {` +${overflowCount} ${overflowCount === 1 ? 'other' : 'others'}`}
        </span>
      )}
    </>
  );
};

function ConversationHeader({
  conversation,
  onReloadConversation,
  onActionComplete,
  sidebarExpanded,
  onToggleSidebar,
  onBack,
  activeMailbox = null,
  dropdownOverride = null,
  pitchedEntities = null,
}) {
  const currentReceiverSlug = globalThis.Qwoted.CURRENT_SLUG;
  const currentReceiverProfileId = globalThis.Qwoted.CURRENT_PROFILE_ID;
  // Identify the viewer by stable profile id; slug is a fallback for id-less/optimistic rows.
  const isViewer = (p) => (currentReceiverProfileId != null && p.id === currentReceiverProfileId)
    || p.slug === currentReceiverSlug;
  // Per product spec: only promoters can add people to a conversation.
  const isMedia = globalThis.Qwoted.CURRENT_PROFILE_TYPE === 'type_media';
  const {
 invitableTeammates, representedExperts, currentTeamId, participantSlugs,
} = useConversationTeammates(conversation);

  // Modal state lifted here so the mobile dropdown can open the same
  // Add-to-Conversation modal that the desktop button opens.
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const openAddContactModal = useCallback(() => setShowAddContactModal(true), []);

  if (!conversation) return null;

  const hasInvitablePeople = !isMedia && (invitableTeammates.length > 0 || representedExperts.length > 0);
  // Peer DM threads can't accept new participants (backend hard-rejects with 422);
  // hide the add-participant affordances on those threads. Adding someone starts a
  // conversation with them, so it needs the same permission as starting one.
  const canAddToConversation = hasInvitablePeople && !isPeerDmOrigin(conversation.origin_type)
    && mayStartConversations();
  const isMuted = Boolean(conversation.muted_at ?? conversation.receipt_muted_at);

  // Get all other participants (excluding current user)
  const otherReceiverProfiles = conversation.conversation_receivers.filter((p) => !isViewer(p));
  const otherSenderProfiles = conversation.conversation_senders.filter((p) => !isViewer(p));

  // Combine all other participants, avoiding duplicates by slug
  const seenSlugs = new Set();
  const allOtherProfiles = [...otherSenderProfiles, ...otherReceiverProfiles].filter((p) => {
    if (seenSlugs.has(p.slug)) return false;
    seenSlugs.add(p.slug);
    return true;
  });

  // On message-source threads the participant is the PR rep. Pick who headlines based on the
  // viewer's role: expert viewing → media first; PR or media viewing → represented expert first.
  // The entity is either an Expert or a Product (same JSONB shape, different link target).
  const rawOriginExpert = conversation.origin_expert;
  const rawOriginProduct = conversation.origin_product;
  // The viewer is "the expert" only when they are the self-represented profile behind this thread.
  // Key on the stable profile id, not the mutable slug; the null guard avoids undefined===undefined.
  const viewerIsExpert = currentReceiverProfileId != null
    && rawOriginExpert?.self_represented_profile_id === currentReceiverProfileId;
  // Products can't be viewers, so the expert-viewer short-circuit only applies on expert origins.
  const originEntity = viewerIsExpert ? null : rawOriginExpert || rawOriginProduct;
  const originEntityUrl = rawOriginProduct && originEntity === rawOriginProduct ? productUrl : expertUrl;
  const isMediaProfile = (p) => p.type_of_profile === 'type_media';

  let headlinedProfiles;
  let urlFor;
  if (viewerIsExpert) {
    headlinedProfiles = [
      ...allOtherProfiles.filter(isMediaProfile),
      ...allOtherProfiles.filter((p) => !isMediaProfile(p)),
    ];
    urlFor = profileUrl;
  } else if (originEntity) {
    headlinedProfiles = [originEntity, ...allOtherProfiles.filter((p) => p.slug !== originEntity.slug)];
    urlFor = (p) => (p.slug === originEntity.slug ? originEntityUrl(p) : profileUrl(p));
  } else {
    headlinedProfiles = allOtherProfiles;
    urlFor = profileUrl;
  }

  const headliner = headlinedProfiles.length === 1 ? headlinedProfiles[0] : null;
  // Subtitle only when a single person is headlined; multi-name headers stay tight.
  const headlinerTitle = headliner ? headliner.title || headliner.title_at_workplace || '' : '';
  // One source is what the design shows; a multi-source pitch keeps the rest in the message body.
  const proposedSource = (pitchedEntities || [])[0] || null;
  const proposedSourceMore = Math.max((pitchedEntities || []).length - 1, 0);

  return (
    <div
      className={classnames(
        'conversation-header w-100 d-flex justify-content-between align-items-center px-4 py-3',
        { 'has-proposed-source': !!proposedSource },
      )}
      style={{ minHeight: 91 }}
    >
      <div className="conversation-header-left d-flex align-items-center">
        {onBack && (
          <button
            type="button"
            className="btn btn-icon inbox-back-btn me-3 d-lg-none"
            onClick={onBack}
            aria-label="Back to inbox"
            title="Back to inbox"
          >
            <i className="fa-solid fa-arrow-left" />
          </button>
        )}

        {onToggleSidebar && (
          <button
            type="button"
            className="btn btn-icon inbox-close-btn"
            onClick={onToggleSidebar}
            aria-label="Hide profile"
            title="Hide profile"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        )}

        {headliner && (
          <a
            href={urlFor(headliner)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View ${headliner.name}'s profile`}
          >
            <ImageCropped
              roundedCircle
              src={headliner.profile_image_url}
              alt={headliner.name}
              size={56}
              wrapperClasses="d-none d-md-inline-block me-2"
              fallbackToInitials
            />
          </a>
        )}

        <div className="conversation-header-title d-flex flex-column align-items-start justify-content-center">
          {headlinedProfiles.length > 0 && <p className="mb-1 lh-1">{formatProfileNames(headlinedProfiles, urlFor)}</p>}

          {headlinerTitle && <p className="paragraph-md text-secondary mb-0 lh-1">{headlinerTitle}</p>}

          {proposedSource && (
            <div className="csc-compact-holder mt-1">
              <ProposedSourceChip entity={proposedSource} moreCount={proposedSourceMore} compact />
            </div>
          )}
        </div>
      </div>

      {proposedSource && (
        <div className="conversation-header-proposed">
          <ProposedSourceChip entity={proposedSource} moreCount={proposedSourceMore} />
        </div>
      )}

      <div className="conversation-header-actions d-flex gap-2 align-items-center">
        {isMuted && (
          <span
            className="text-neutral-60 d-inline-flex align-items-center gap-2"
            style={{ fontSize: '14px' }}
          >
            <i className="fa-solid fa-bell-slash" />
            Muted Alerts
          </span>
        )}

        {canAddToConversation && !conversation.receipt_archived_at && (
          <AddToConversationButton
            conversationId={conversation.conversation_id}
            currentTeamId={currentTeamId}
            participantSlugs={participantSlugs}
            representedExperts={representedExperts}
            onSuccess={onReloadConversation}
            show={showAddContactModal}
            onShowChange={setShowAddContactModal}
            buttonClassName="d-none d-md-inline-flex"
          />
        )}

        {dropdownOverride ?? (onActionComplete && !conversation.receipt_archived_at && (
          <ConversationDropdown
            conversation={conversation}
            onActionComplete={onActionComplete}
            onAddContact={canAddToConversation ? openAddContactModal : undefined}
            onToggleSidebar={onToggleSidebar}
            sidebarExpanded={sidebarExpanded}
            activeMailbox={activeMailbox}
          />
        ))}

        {onToggleSidebar && (
          <button
            type="button"
            className={classnames(
              'btn btn-secondary btn-icon rounded-2 text-neutral-80 fs-5 border-0 shadow-none d-none d-md-inline-flex',
              { active: sidebarExpanded },
            )}
            onClick={onToggleSidebar}
            title={sidebarExpanded ? 'Hide sidebar' : 'Show sidebar'}
          >
            <i className="fa-solid fa-sidebar-flip" />
          </button>
        )}
      </div>
    </div>
  );
}

ConversationHeader.propTypes = {
  dropdownOverride: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types -- the pitches view's jsonb rows, passed through
  pitchedEntities: PropTypes.array,
  conversation: PropTypes.shape({
    conversation_id: PropTypes.string.isRequired,
    conversation_receivers: PropTypes.arrayOf(PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string })),
    conversation_senders: PropTypes.arrayOf(PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string })),
    origin_expert: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
      profile_image_url: PropTypes.string,
      self_represented: PropTypes.bool,
      self_represented_profile_id: PropTypes.number,
    }),
    origin_product: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
      profile_image_url: PropTypes.string,
    }),
  }),
  onReloadConversation: PropTypes.func,
  onActionComplete: PropTypes.func,
  sidebarExpanded: PropTypes.bool,
  onToggleSidebar: PropTypes.func,
  onBack: PropTypes.func,
  activeMailbox: PropTypes.string,
};

export default ConversationHeader;
